// TypeScript port of CodeTestIDE's background/panel/js/UI/plain-english.js —
// same template engine also vendored into ataserver/xpeerAI_APIServer's
// cdp-bootstrap.js for live capture. Used here as a DISPLAY-TIME fallback:
// commands recorded before englishDescription existed (or before a given
// backend fix landed) never got one written into their stored/persisted
// data, and can't get one retroactively without re-recording — but every
// command still carries enough raw locator/value data to compose the same
// sentence on the fly, so RecordedStepsService.descriptionFor() calls
// buildPlainEnglishSentence() itself whenever a command's own
// englishDescription is missing, instead of just showing nothing.

export interface PlainEnglishTargetInfo {
  name?: string;
  tag?: string;
  role?: string;
  context?: string;
  fieldLabel?: string;
}

function humanizeCommandName(name: string | undefined | null): string {
  if (!name) return '';
  return String(name)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
}

function humanizeTargetName(target: unknown): string {
  if (target === undefined || target === null) return 'the element';
  let t = String(target).trim();
  if (!t) return 'the element';
  t = t.replace(/^(id|name|css|xpath|linktext|link)=/i, '');
  t = t.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  t = t.trim();
  return t || 'the element';
}

const TAG_NOUN: Record<string, string> = {
  button: 'button', a: 'link', input: 'input field', textarea: 'text area',
  select: 'dropdown', h1: 'heading', h2: 'heading', h3: 'heading',
  h4: 'heading', h5: 'heading', h6: 'heading', li: 'list item',
  img: 'image', label: 'label', p: 'text', span: 'text', div: 'element',
};

const ROLE_NOUN: Record<string, string> = {
  button: 'button', link: 'link', checkbox: 'checkbox', radio: 'radio button',
  combobox: 'dropdown', listbox: 'dropdown', textbox: 'input field',
  searchbox: 'search field', heading: 'heading', switch: 'toggle',
  slider: 'slider', spinbutton: 'number field', tab: 'tab', menuitem: 'menu item',
};

function isGenericSymbolName(name: string): boolean {
  const t = (name || '').trim();
  return t.length > 0 && t.length <= 3 && /^[^a-zA-Z0-9\s]+$/.test(t);
}

function clean(s: unknown): string {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

function quote(value: unknown): string {
  return "'" + String(value).replace(/'/g, "\\'") + "'";
}

function formatTargetPhrase(nameOrInfo: string | PlainEnglishTargetInfo | undefined | null): string {
  if (typeof nameOrInfo === 'string' && /^(The|An)\s/.test(nameOrInfo.trim())) {
    const phrase = nameOrInfo.trim().replace(/\.$/, '').replace(/^(The|An)\s+/, '');
    return 'the ' + phrase;
  }
  if (nameOrInfo && typeof nameOrInfo === 'object') {
    const name = nameOrInfo.name ? clean(nameOrInfo.name) : '';
    const context = nameOrInfo.context ? clean(nameOrInfo.context) : '';
    const fieldLabel = nameOrInfo.fieldLabel ? clean(nameOrInfo.fieldLabel) : '';
    const noun = (nameOrInfo.role && ROLE_NOUN[nameOrInfo.role])
      || (nameOrInfo.tag && TAG_NOUN[nameOrInfo.tag])
      || null;
    if (context && isGenericSymbolName(name)) {
      return `the "${name}" ${noun || 'button'} in the "${context}" group`;
    }
    if (fieldLabel && name) {
      return `the "${name}" ${noun || 'field'} in the "${fieldLabel}" section`;
    }
    if (fieldLabel && noun) {
      return `the "${fieldLabel}" section's ${noun}`;
    }
    if (name && noun) return `the "${name}" ${noun}`;
    if (name) return `"${name}"`;
    if (noun) return 'the ' + noun;
    return 'the element';
  }
  return humanizeTargetName(nameOrInfo);
}

function describeKeySequence(value: unknown): string | null {
  const map: Record<string, string> = {
    '${KEY_ENTER}': 'press Enter', '${KEY_UP}': 'press the Up arrow key',
    '${KEY_DOWN}': 'press the Down arrow key', '${KEY_TAB}': 'press Tab',
    '${KEY_ESC}': 'press Escape',
  };
  return map[value as string] || null;
}

type Template = (target: string, value?: unknown, valueTarget?: unknown) => string;

const TEMPLATES: Record<string, Template> = {
  open: (target) => 'Open ' + (target && target !== 'the element' ? quote(target) : 'the page'),
  click: (target) => 'Click on ' + target,
  doubleClick: (target) => 'Double-click on ' + target,
  rightclick: (target) => 'Right-click on ' + target,
  contextMenu: (target) => 'Right-click on ' + target,
  mouseover: (target) => 'Hover over ' + target,
  mouseOut: (target) => 'Move the mouse away from ' + target,
  type: (target, value) => (value ? 'Type ' + quote(value) + ' into ' + target : 'Clear the text in ' + target),
  sendKeys: (target, value) => {
    const keyDesc = describeKeySequence(value);
    if (keyDesc) return keyDesc.charAt(0).toUpperCase() + keyDesc.slice(1) + ' in ' + target;
    return 'Send ' + quote(value) + ' to ' + target;
  },
  submit: (target) => 'Submit the form near ' + target,
  select: (target, value) => 'Select ' + quote(value) + ' from ' + target,
  dropdownSelect: (target, value) => 'Select ' + quote(value) + ' from the ' + target + ' dropdown',
  addSelection: (target, value) => 'Add ' + quote(value) + ' to the selection in ' + target,
  removeSelection: (target, value) => 'Remove ' + quote(value) + ' from the selection in ' + target,
  dragAndDropToObject: (target, value, valueTarget) => 'Drag ' + target + ' and drop it onto ' + (valueTarget || 'the target element'),
  dragAndDropToOffset: (target, value) => 'Drag ' + target + ' by offset ' + value,
  editContent: (target, value) => 'Edit the content of ' + target + ' to ' + quote(value),
  waitForElementPresent: (target) => 'Wait until ' + target + ' appears',
  verifyText: (target, value) => 'Verify that ' + target + ' shows the text ' + quote(value),
  assertText: (target, value) => 'Assert that ' + target + ' shows the text ' + quote(value),
  verifyTextContains: (target, value) => 'Verify that ' + target + ' contains the text ' + quote(value),
  storeText: (target) => 'Store the text of ' + target,
  storeValue: (target) => 'Store the value of ' + target,
  verifyElementChecked: (target) => 'Verify that ' + target + ' is checked',
  verifyElementUnChecked: (target) => 'Verify that ' + target + ' is unchecked',
  assertElementChecked: (target) => 'Assert that ' + target + ' is checked',
  assertElementUnChecked: (target) => 'Assert that ' + target + ' is unchecked',
  verifySelectDropdownText: (target, value) => 'Verify that ' + target + ' dropdown shows ' + quote(value),
  assertSelectDropdownText: (target, value) => 'Assert that ' + target + ' dropdown shows ' + quote(value),
  findRowByValue: (target, value) => 'Find row where ' + target + ' equals ' + quote(value) + ':',
  runScript: () => 'Run a custom script',
  canvasClick: (target, value) => 'Click on the canvas at ' + value,
  canvasRightClick: (target, value) => 'Right-click on the canvas at ' + value,
  canvasDoubleClick: (target, value) => 'Double-click on the canvas at ' + value,
  scroll: (target) => 'Scroll to ' + target,
  scrollIntoView: (target) => 'Scroll ' + target + ' into view',
  scrollUp: () => 'Scroll up',
  scrollDown: () => 'Scroll down',
  scrollToTop: () => 'Scroll to the top of the page',
  scrollToBottom: () => 'Scroll to the bottom of the page',
  selectFrame: (target) => {
    const t = String(target || '').toLowerCase();
    if (t.indexOf('relative=top') > -1) return 'Switch to the default content (top-level page)';
    if (t.indexOf('relative=parent') > -1) return 'Switch to the parent frame';
    return 'Switch to the iframe ' + target;
  },
  switchToFrame: (target) => 'Switch to the iframe ' + target,
  switchToDefaultContent: () => 'Switch back to the default content (top-level page)',
  selectWindow: (target) => 'Switch to the window ' + target,
  selectPopUp: (target) => 'Switch to the pop-up window ' + target,
  deselectPopUp: () => 'Switch back to the main window',
  openWindow: (target) => 'Open a new window at ' + quote(target),
  waitForPopUp: (target) => 'Wait for the pop-up window ' + target + ' to appear',
  chooseOkOnNextConfirmation: () => 'Click OK on the next confirmation dialog',
  chooseCancelOnNextConfirmation: () => 'Click Cancel on the next confirmation dialog',
  chooseOkOnNextPrompt: () => 'Accept the next prompt dialog',
  chooseCancelOnNextPrompt: () => 'Cancel the next prompt dialog',
  answerOnNextPrompt: (target, value) => 'Answer the next prompt dialog with ' + quote(value),
  assertAlert: (target, value) => 'Assert that the alert says ' + quote(value),
  verifyAlert: (target, value) => 'Verify that the alert says ' + quote(value),
  assertPrompt: (target, value) => 'Assert that the prompt says ' + quote(value),
  assertConfirmation: (target, value) => 'Assert that the confirmation dialog says ' + quote(value),
  elseIfElementPresent: (target) => 'Otherwise, if ' + target + ' is present, then:',
  ifElementPresent: (target) => 'If ' + target + ' is present, then:',
  'else statement': () => 'Otherwise:',
  'close statement': () => 'End of the conditional block',
  'find row by value end': () => 'End of the find row-matching loop',
  waitForPageToLoad: () => 'Wait for the page to finish loading',
  waitForFrameToLoad: (target) => 'Wait for the iframe ' + target + ' to finish loading',
  waitForCondition: (target, value) => 'Wait until the condition ' + quote(value) + ' is true',
  focus: (target) => 'Focus on ' + target,
  keyDown: (target, value) => 'Hold down the ' + quote(value) + ' key on ' + target,
  keyUp: (target, value) => 'Release the ' + quote(value) + ' key on ' + target,
  keyPress: (target, value) => 'Press the ' + quote(value) + ' key on ' + target,
  check: (target) => 'Check ' + target,
  uncheck: (target) => 'Uncheck ' + target,
  removeAllSelections: (target) => 'Clear all selections in ' + target,
  close: (target) => 'Close ' + target,
};

function capitalize(s: unknown): string {
  const str = String(s || '');
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function buildPlainEnglishSentence(
  commandName: string,
  targetInfo: string | PlainEnglishTargetInfo | undefined | null,
  value?: unknown,
  valueTarget?: unknown,
): string {
  const target = formatTargetPhrase(targetInfo);
  const template = TEMPLATES[commandName];
  if (!template) {
    const verbPhrase = humanizeCommandName(commandName);
    if (value) return capitalize(verbPhrase) + ' ' + target + ' with ' + quote(value);
    return capitalize(verbPhrase) + ' ' + target;
  }
  return capitalize(template(target, value, valueTarget));
}
